import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, FileText, Users, Package, ShoppingCart, TrendingUp, 
  Edit, Trash2, Eye, Download, Printer, IndianRupee, Calendar,
  Building2, Search, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/client-portal/business`;

const INDIAN_STATES = [
  { code: "27", name: "Maharashtra" },
  { code: "07", name: "Delhi" },
  { code: "29", name: "Karnataka" },
  { code: "33", name: "Tamil Nadu" },
  { code: "24", name: "Gujarat" },
  { code: "36", name: "Telangana" },
  { code: "19", name: "West Bengal" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "08", name: "Rajasthan" },
  { code: "32", name: "Kerala" },
  { code: "21", name: "Odisha" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "06", name: "Haryana" },
  { code: "10", name: "Bihar" }
];

const GST_RATES = [0, 5, 12, 18, 28];

const ClientBusinessInvoicing = ({ token }) => {
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [gstSummary, setGstSummary] = useState(null);
  
  // Modal states
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Form states
  const [customerForm, setCustomerForm] = useState({
    name: '', gstin: '', customer_type: 'B2C', email: '', phone: '',
    address: '', city: '', state: 'Maharashtra', state_code: '27', pincode: ''
  });
  const [vendorForm, setVendorForm] = useState({
    name: '', gstin: '', email: '', phone: '',
    address: '', city: '', state: 'Maharashtra', state_code: '27', pincode: ''
  });
  const [productForm, setProductForm] = useState({
    name: '', description: '', hsn_code: '', item_type: 'goods',
    unit: 'NOS', selling_price: 0, purchase_price: 0, gst_rate: 18
  });
  const [invoiceForm, setInvoiceForm] = useState({
    customer_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', place_of_supply: '27', notes: '', terms: '', items: []
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  
  const axiosConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Fetch all data
  useEffect(() => {
    fetchSettings();
    fetchCustomers();
    fetchVendors();
    fetchProducts();
    fetchSalesInvoices();
    fetchGstSummary();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, axiosConfig());
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, axiosConfig());
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, axiosConfig());
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, axiosConfig());
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSalesInvoices = async () => {
    try {
      const response = await axios.get(`${API}/sales-invoices`, axiosConfig());
      setSalesInvoices(response.data);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    }
  };

  const fetchGstSummary = async () => {
    const now = new Date();
    try {
      const response = await axios.get(
        `${API}/reports/gst-summary?month=${now.getMonth() + 1}&year=${now.getFullYear()}`,
        axiosConfig()
      );
      setGstSummary(response.data);
    } catch (error) {
      console.error('Error fetching GST summary:', error);
    }
  };

  // CRUD Operations
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/customers`, customerForm, axiosConfig());
      setShowCustomerModal(false);
      setCustomerForm({ name: '', gstin: '', customer_type: 'B2C', email: '', phone: '', address: '', city: '', state: 'Maharashtra', state_code: '27', pincode: '' });
      fetchCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/vendors`, vendorForm, axiosConfig());
      setShowVendorModal(false);
      setVendorForm({ name: '', gstin: '', email: '', phone: '', address: '', city: '', state: 'Maharashtra', state_code: '27', pincode: '' });
      fetchVendors();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/products`, productForm, axiosConfig());
      setShowProductModal(false);
      setProductForm({ name: '', description: '', hsn_code: '', item_type: 'goods', unit: 'NOS', selling_price: 0, purchase_price: 0, gst_rate: 18 });
      fetchProducts();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, axiosConfig());
      fetchCustomers();
    } catch (error) {
      alert('Failed to delete customer');
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${API}/vendors/${id}`, axiosConfig());
      fetchVendors();
    } catch (error) {
      alert('Failed to delete vendor');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, axiosConfig());
      fetchProducts();
    } catch (error) {
      alert('Failed to delete product');
    }
  };

  // Invoice creation
  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, { product_id: '', quantity: 1, rate: 0, discount_percent: 0 }]);
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;
    
    // Auto-fill rate when product is selected
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].rate = product.selling_price;
      }
    }
    
    setInvoiceItems(newItems);
  };

  const removeInvoiceItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const calculateInvoiceTotal = () => {
    let total = 0;
    invoiceItems.forEach(item => {
      const product = products.find(p => p.id === item.product_id);
      if (product) {
        const gross = item.quantity * item.rate;
        const discount = gross * (item.discount_percent / 100);
        const taxable = gross - discount;
        const gst = taxable * (product.gst_rate / 100);
        total += taxable + gst;
      }
    });
    return Math.round(total);
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (invoiceItems.length === 0) {
      alert('Please add at least one item');
      return;
    }
    
    setLoading(true);
    try {
      const invoiceData = {
        ...invoiceForm,
        items: invoiceItems.map(item => ({
          product_id: item.product_id,
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate),
          discount_percent: parseFloat(item.discount_percent || 0)
        }))
      };
      
      await axios.post(`${API}/sales-invoices`, invoiceData, axiosConfig());
      setShowInvoiceModal(false);
      setInvoiceForm({ customer_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', place_of_supply: '27', notes: '', terms: '', items: [] });
      setInvoiceItems([]);
      fetchSalesInvoices();
      fetchGstSummary();
      fetchSettings(); // To get updated invoice number
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, settings, axiosConfig());
      setShowSettingsModal(false);
      fetchSettings();
    } catch (error) {
      alert('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Business Invoicing</h2>
          <p className="text-gray-500 text-sm mt-1">Manage your sales, purchases and GST</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowSettingsModal(true)}
          data-testid="business-settings-btn"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Business Settings
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <IndianRupee className="w-6 h-6 mx-auto mb-1 opacity-80" />
            <div className="text-xl font-bold">₹{(gstSummary?.sales_summary?.total_amount || 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">This Month Sales</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 mx-auto mb-1 opacity-80" />
            <div className="text-xl font-bold">{salesInvoices.length}</div>
            <div className="text-xs opacity-80">Total Invoices</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-1 opacity-80" />
            <div className="text-xl font-bold">{customers.length}</div>
            <div className="text-xs opacity-80">Customers</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-1 opacity-80" />
            <div className="text-xl font-bold">₹{(gstSummary?.net_liability || 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">GST Liability</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="sales" data-testid="tab-sales">
            <FileText className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Sales</span>
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Users className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Customers</span>
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Products</span>
          </TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">
            <ShoppingCart className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Vendors</span>
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Reports</span>
          </TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Sales Invoices</h3>
            <Button
              onClick={() => setShowInvoiceModal(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-600"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </div>
          
          {salesInvoices.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No invoices yet</h3>
                <p className="text-gray-500 mb-4">Create your first GST invoice</p>
                <Button onClick={() => setShowInvoiceModal(true)} className="bg-gradient-to-r from-green-500 to-emerald-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {salesInvoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{invoice.invoice_number}</td>
                          <td className="px-4 py-3 text-gray-600">{invoice.invoice_date}</td>
                          <td className="px-4 py-3 text-gray-600">{invoice.customer_name}</td>
                          <td className="px-4 py-3 text-right font-semibold">₹{invoice.total_amount?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              invoice.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              invoice.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {invoice.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowInvoiceView(invoice)}
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Customers</h3>
            <Button
              onClick={() => setShowCustomerModal(true)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </div>
          
          {customers.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No customers yet</h3>
                <p className="text-gray-500 mb-4">Add your customers to create invoices</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {customers.map((customer) => (
                <Card key={customer.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900">{customer.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          customer.customer_type === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {customer.customer_type}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCustomer(customer.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {customer.gstin && <p className="text-sm text-gray-600 mt-2">GSTIN: {customer.gstin}</p>}
                    {customer.phone && <p className="text-sm text-gray-500">{customer.phone}</p>}
                    <p className="text-sm font-medium mt-2">
                      Balance: <span className={customer.current_balance > 0 ? 'text-red-600' : 'text-green-600'}>
                        ₹{customer.current_balance?.toLocaleString()}
                      </span>
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Products & Services</h3>
            <Button
              onClick={() => setShowProductModal(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-600"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
          
          {products.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No products yet</h3>
                <p className="text-gray-500 mb-4">Add your products or services to use in invoices</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {products.map((product) => (
                <Card key={product.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900">{product.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          product.item_type === 'goods' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {product.item_type === 'goods' ? 'Goods' : 'Service'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {product.hsn_code && <p className="text-sm text-gray-600 mt-2">HSN: {product.hsn_code}</p>}
                    <div className="mt-2 flex justify-between text-sm">
                      <span>Price: ₹{product.selling_price}</span>
                      <span className="text-orange-600">GST: {product.gst_rate}%</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Vendors</h3>
            <Button
              onClick={() => setShowVendorModal(true)}
              className="bg-gradient-to-r from-orange-500 to-red-600"
              data-testid="add-vendor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </div>
          
          {vendors.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No vendors yet</h3>
                <p className="text-gray-500 mb-4">Add your vendors/suppliers to record purchases</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {vendors.map((vendor) => (
                <Card key={vendor.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <h4 className="font-semibold text-gray-900">{vendor.name}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteVendor(vendor.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {vendor.gstin && <p className="text-sm text-gray-600 mt-2">GSTIN: {vendor.gstin}</p>}
                    {vendor.phone && <p className="text-sm text-gray-500">{vendor.phone}</p>}
                    <p className="text-sm font-medium mt-2">
                      Payable: <span className={vendor.current_balance > 0 ? 'text-red-600' : 'text-green-600'}>
                        ₹{vendor.current_balance?.toLocaleString()}
                      </span>
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <h3 className="text-lg font-semibold">GST Reports</h3>
          
          {gstSummary && (
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sales Summary</CardTitle>
                  <CardDescription>{new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Invoices</span>
                    <span className="font-semibold">{gstSummary.sales_summary?.total_invoices || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B2B Sales</span>
                    <span className="font-semibold">₹{(gstSummary.sales_summary?.b2b_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B2C Sales</span>
                    <span className="font-semibold">₹{(gstSummary.sales_summary?.b2c_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-semibold">Total Sales</span>
                    <span className="font-bold text-green-600">₹{(gstSummary.sales_summary?.total_amount || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">GST Liability</CardTitle>
                  <CardDescription>Output Tax - Input Tax</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Output CGST</span>
                    <span className="font-semibold">₹{(gstSummary.output_tax?.cgst || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Output SGST</span>
                    <span className="font-semibold">₹{(gstSummary.output_tax?.sgst || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Output IGST</span>
                    <span className="font-semibold">₹{(gstSummary.output_tax?.igst || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-semibold">Net Liability</span>
                    <span className="font-bold text-red-600">₹{(gstSummary.net_liability || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>Add a new customer for invoicing</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={customerForm.name} onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})} required />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={customerForm.customer_type} onValueChange={(v) => setCustomerForm({...customerForm, customer_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2C">B2C (Consumer)</SelectItem>
                    <SelectItem value="B2B">B2B (Business)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GSTIN {customerForm.customer_type === 'B2B' && '*'}</Label>
                <Input value={customerForm.gstin} onChange={(e) => setCustomerForm({...customerForm, gstin: e.target.value})} placeholder="22AAAAA0000A1Z5" required={customerForm.customer_type === 'B2B'} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={customerForm.address} onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})} />
              </div>
              <div>
                <Label>City</Label>
                <Input value={customerForm.city} onChange={(e) => setCustomerForm({...customerForm, city: e.target.value})} />
              </div>
              <div>
                <Label>State</Label>
                <Select value={customerForm.state_code} onValueChange={(v) => {
                  const state = INDIAN_STATES.find(s => s.code === v);
                  setCustomerForm({...customerForm, state_code: v, state: state?.name || ''});
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCustomerModal(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600">
                {loading ? 'Creating...' : 'Add Customer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Modal */}
      <Dialog open={showVendorModal} onOpenChange={setShowVendorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Vendor</DialogTitle>
            <DialogDescription>Add a new vendor/supplier</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateVendor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={vendorForm.name} onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})} required />
              </div>
              <div className="col-span-2">
                <Label>GSTIN</Label>
                <Input value={vendorForm.gstin} onChange={(e) => setVendorForm({...vendorForm, gstin: e.target.value})} placeholder="22AAAAA0000A1Z5" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={vendorForm.phone} onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowVendorModal(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-600">
                {loading ? 'Creating...' : 'Add Vendor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Product/Service</DialogTitle>
            <DialogDescription>Add to your catalog</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={productForm.item_type} onValueChange={(v) => setProductForm({...productForm, item_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="goods">Goods</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>HSN/SAC Code</Label>
                <Input value={productForm.hsn_code} onChange={(e) => setProductForm({...productForm, hsn_code: e.target.value})} />
              </div>
              <div>
                <Label>Selling Price *</Label>
                <Input type="number" value={productForm.selling_price} onChange={(e) => setProductForm({...productForm, selling_price: parseFloat(e.target.value) || 0})} required />
              </div>
              <div>
                <Label>GST Rate *</Label>
                <Select value={productForm.gst_rate.toString()} onValueChange={(v) => setProductForm({...productForm, gst_rate: parseFloat(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map(rate => (
                      <SelectItem key={rate} value={rate.toString()}>{rate}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={productForm.unit} onValueChange={(v) => setProductForm({...productForm, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NOS">Numbers</SelectItem>
                    <SelectItem value="KG">Kilograms</SelectItem>
                    <SelectItem value="LTR">Litres</SelectItem>
                    <SelectItem value="MTR">Meters</SelectItem>
                    <SelectItem value="PCS">Pieces</SelectItem>
                    <SelectItem value="HRS">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowProductModal(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600">
                {loading ? 'Creating...' : 'Add Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Sales Invoice</DialogTitle>
            <DialogDescription>Next Invoice: {settings?.invoice_prefix}-{String(settings?.next_invoice_number || 1).padStart(5, '0')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Customer *</Label>
                <Select value={invoiceForm.customer_id} onValueChange={(v) => setInvoiceForm({...invoiceForm, customer_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invoice Date *</Label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} required />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})} />
              </div>
            </div>

            {/* Invoice Items */}
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base font-semibold">Invoice Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addInvoiceItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              
              {invoiceItems.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Click "Add Item" to add products</p>
              ) : (
                <div className="space-y-3">
                  {invoiceItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5">
                        <Select value={item.product_id} onValueChange={(v) => updateInvoiceItem(index, 'product_id', v)}>
                          <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                          <SelectContent>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name} (₹{p.selling_price})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)} min="1" />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateInvoiceItem(index, 'rate', e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="Disc %" value={item.discount_percent} onChange={(e) => updateInvoiceItem(index, 'discount_percent', e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeInvoiceItem(index)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {invoiceItems.length > 0 && (
                <div className="mt-4 pt-4 border-t text-right">
                  <span className="text-lg font-bold">
                    Estimated Total: ₹{calculateInvoiceTotal().toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Input value={invoiceForm.notes} onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})} placeholder="Additional notes for customer" />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowInvoiceModal(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading || invoiceItems.length === 0 || !invoiceForm.customer_id} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600">
                {loading ? 'Creating...' : 'Create Invoice'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={!!showInvoiceView} onOpenChange={() => setShowInvoiceView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice {showInvoiceView?.invoice_number}</DialogTitle>
          </DialogHeader>
          
          {showInvoiceView && (
            <div className="space-y-6 p-4 bg-white border rounded-lg">
              {/* Header */}
              <div className="flex justify-between border-b pb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{settings?.business_name || 'Your Business'}</h2>
                  {settings?.gstin && <p className="text-sm text-gray-600">GSTIN: {settings.gstin}</p>}
                  <p className="text-sm text-gray-600">{settings?.address}, {settings?.city}</p>
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold text-orange-600">TAX INVOICE</h3>
                  <p className="font-semibold">{showInvoiceView.invoice_number}</p>
                  <p className="text-sm text-gray-600">Date: {showInvoiceView.invoice_date}</p>
                </div>
              </div>

              {/* Bill To */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Bill To:</h4>
                <p className="font-semibold text-gray-900">{showInvoiceView.customer_name}</p>
                {showInvoiceView.customer_gstin && <p className="text-sm">GSTIN: {showInvoiceView.customer_gstin}</p>}
                <p className="text-sm text-gray-600">{showInvoiceView.customer_address}</p>
              </div>

              {/* Items Table */}
              <table className="w-full border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left text-xs">Item</th>
                    <th className="border p-2 text-center text-xs">HSN</th>
                    <th className="border p-2 text-center text-xs">Qty</th>
                    <th className="border p-2 text-right text-xs">Rate</th>
                    <th className="border p-2 text-right text-xs">Taxable</th>
                    <th className="border p-2 text-right text-xs">GST</th>
                    <th className="border p-2 text-right text-xs">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {showInvoiceView.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="border p-2 text-sm">{item.product_name}</td>
                      <td className="border p-2 text-center text-sm">{item.hsn_code || '-'}</td>
                      <td className="border p-2 text-center text-sm">{item.quantity} {item.unit}</td>
                      <td className="border p-2 text-right text-sm">₹{item.rate}</td>
                      <td className="border p-2 text-right text-sm">₹{item.taxable_amount}</td>
                      <td className="border p-2 text-right text-sm">{item.gst_rate}%</td>
                      <td className="border p-2 text-right text-sm font-semibold">₹{item.total_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>Taxable Amount:</span>
                    <span>₹{showInvoiceView.taxable_amount?.toLocaleString()}</span>
                  </div>
                  {!showInvoiceView.is_interstate ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>CGST:</span>
                        <span>₹{showInvoiceView.cgst_amount?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>SGST:</span>
                        <span>₹{showInvoiceView.sgst_amount?.toLocaleString()}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span>IGST:</span>
                      <span>₹{showInvoiceView.igst_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {showInvoiceView.round_off !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Round Off:</span>
                      <span>₹{showInvoiceView.round_off}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span>₹{showInvoiceView.total_amount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Amount in Words */}
              <div className="bg-gray-50 p-3 rounded text-sm">
                <strong>Amount in Words:</strong> {showInvoiceView.amount_in_words}
              </div>

              {/* Bank Details */}
              {settings?.bank_name && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-2">Bank Details:</h4>
                  <p className="text-sm">Bank: {settings.bank_name}</p>
                  <p className="text-sm">A/C: {settings.account_number}</p>
                  <p className="text-sm">IFSC: {settings.ifsc_code}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Business Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Business Settings</DialogTitle>
            <DialogDescription>Configure your business details for invoices</DialogDescription>
          </DialogHeader>
          {settings && (
            <form onSubmit={handleUpdateSettings} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Business Name *</Label>
                  <Input value={settings.business_name || ''} onChange={(e) => setSettings({...settings, business_name: e.target.value})} required />
                </div>
                <div>
                  <Label>GSTIN</Label>
                  <Input value={settings.gstin || ''} onChange={(e) => setSettings({...settings, gstin: e.target.value})} />
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input value={settings.pan || ''} onChange={(e) => setSettings({...settings, pan: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={settings.city || ''} onChange={(e) => setSettings({...settings, city: e.target.value})} />
                </div>
                <div>
                  <Label>State</Label>
                  <Select value={settings.state_code || '27'} onValueChange={(v) => {
                    const state = INDIAN_STATES.find(s => s.code === v);
                    setSettings({...settings, state_code: v, state: state?.name || ''});
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Invoice Prefix</Label>
                  <Input value={settings.invoice_prefix || 'INV'} onChange={(e) => setSettings({...settings, invoice_prefix: e.target.value})} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={settings.phone || ''} onChange={(e) => setSettings({...settings, phone: e.target.value})} />
                </div>
                <div className="col-span-2 border-t pt-4">
                  <h4 className="font-semibold mb-3">Bank Details (for invoices)</h4>
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input value={settings.bank_name || ''} onChange={(e) => setSettings({...settings, bank_name: e.target.value})} />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input value={settings.account_number || ''} onChange={(e) => setSettings({...settings, account_number: e.target.value})} />
                </div>
                <div>
                  <Label>IFSC Code</Label>
                  <Input value={settings.ifsc_code || ''} onChange={(e) => setSettings({...settings, ifsc_code: e.target.value})} />
                </div>
                <div>
                  <Label>UPI ID</Label>
                  <Input value={settings.upi_id || ''} onChange={(e) => setSettings({...settings, upi_id: e.target.value})} />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-600">
                  {loading ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientBusinessInvoicing;
